/**
 * R2 Image URL Builder & API
 * Feature: 007-cloudflare-migration, 006-session-auth-migration
 *
 * Utility for building Cloudflare R2 image URLs with transforms
 * and API functions for upload/delete operations.
 * 
 * Image Handler API: api.suersdey.com/v1/images
 * - POST /v1/images/upload - Upload single image
 * - POST /v1/images/upload/batch - Upload multiple images (max 10)
 * - GET /v1/images/:key - Download with transforms
 * - DELETE /v1/images/:key - Delete image (admin only)
 */

import * as Sentry from '@sentry/react';
import { getAccessToken } from './client';
import { ApiError, ApiErrorCode } from './errors';

// ============================================================================
// Configuration
// ============================================================================

/** API base URL - uses same gateway as other APIs */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.suersdey.com/v1'

/** Images API base - derives from API base, replacing /v1 with /v1/images */
const IMAGES_API_URL = API_BASE_URL.replace(/\/v1\/?$/, '/v1/images')

/** Images CDN URL for downloads/transforms (can be separate CDN or same as API) */
const IMAGES_BASE_URL =
  import.meta.env.VITE_IMAGES_BASE_URL || IMAGES_API_URL

// ============================================================================
// Types
// ============================================================================

/**
 * R2 bucket types
 */
export type ImageBucket =
  | 'marketplace-images'
  | 'business-images'
  | 'profile-avatars'
  | 'review-photos'

/**
 * Image transform fit modes
 */
export type ImageFit = 'scale-down' | 'contain' | 'cover' | 'crop'

/**
 * Image output formats
 */
export type ImageFormat = 'auto' | 'webp' | 'avif' | 'jpeg' | 'png'

/**
 * Image transform options
 */
export interface ImageTransformOptions {
  /** Target width in pixels (1-4096) */
  width?: number
  /** Target height in pixels (1-4096) */
  height?: number
  /** Output format (default: webp) */
  format?: ImageFormat
  /** Fit mode (default: cover) */
  fit?: ImageFit
  /** Output quality 1-100 (default: 85) */
  quality?: number
}

/**
 * Preset transform configurations
 */
export interface ImagePreset {
  width: number
  height?: number
  fit: ImageFit
  format: ImageFormat
  quality?: number
}

// ============================================================================
// Presets
// ============================================================================

/**
 * Common image size presets
 */
export const IMAGE_PRESETS: Record<string, ImagePreset> = {
  /** Avatar thumbnail (40x40) */
  avatar: {
    width: 40,
    height: 40,
    fit: 'cover',
    format: 'webp',
  },
  /** Small thumbnail (80x80) */
  thumbnail: {
    width: 80,
    height: 80,
    fit: 'cover',
    format: 'webp',
  },
  /** Card image (300x200) */
  card: {
    width: 300,
    height: 200,
    fit: 'cover',
    format: 'webp',
  },
  /** List item image (120x80) */
  listItem: {
    width: 120,
    height: 80,
    fit: 'cover',
    format: 'webp',
  },
  /** Detail page image (600x400) */
  detail: {
    width: 600,
    height: 400,
    fit: 'contain',
    format: 'webp',
  },
  /** Full width image (800) */
  full: {
    width: 800,
    fit: 'scale-down',
    format: 'webp',
  },
  /** Large image (1200) */
  large: {
    width: 1200,
    fit: 'scale-down',
    format: 'webp',
  },
}

// ============================================================================
// URL Builder
// ============================================================================

/**
 * Build R2 image URL with optional transforms
 *
 * @param key - Image key (path including bucket)
 * @param options - Transform options or preset name
 * @returns Full image URL with transform parameters
 *
 * @example
 * // With options
 * buildImageUrl('business/abc/photo.jpg', { width: 300, format: 'webp' })
 * // => 'https://api.suersdey.com/v1/images/business/abc/photo.jpg?width=300&format=webp'
 *
 * @example
 * // With preset
 * buildImageUrl('marketplace/user/avatar.jpg', 'thumbnail')
 * // => 'https://api.suersdey.com/v1/images/marketplace/user/avatar.jpg?width=80&height=80&fit=cover&format=webp'
 */
export function buildImageUrl(
  key: string | null | undefined,
  options?: ImageTransformOptions | keyof typeof IMAGE_PRESETS
): string {
  // Return placeholder for null/undefined keys or empty strings
  if (!key || key.trim() === '') {
    return getPlaceholderUrl(options)
  }

  // Resolve preset if string provided
  const transformOptions: ImageTransformOptions | undefined =
    typeof options === 'string' ? IMAGE_PRESETS[options] : options

  // Handle keys that are already full URLs
  if (key.startsWith('http://') || key.startsWith('https://')) {
    try {
      const parsed = new URL(key)
      // Check if it's our images domain - extract key and rebuild
      if (parsed.origin.includes('api.suersdey.com') || parsed.origin.includes('images.suersdey.com')) {
        // Extract key from path (remove /v1/images/ or /images/ prefix)
        let extractedKey = parsed.pathname
          .replace(/^\/v1\/images\//, '')
          .replace(/^\/images\//, '')
          .replace(/^\//, '')
        key = extractedKey
      } else {
        // External URL - return as-is (no transforms)
        return key
      }
    } catch {
      // Invalid URL format, continue with key as-is
    }
  }

  // Clean the key: remove leading slashes
  const cleanKey = key.replace(/^\/+/, '')

  // Build URL safely by concatenating strings (avoid URL constructor path resolution issues)
  const baseUrl = IMAGES_BASE_URL.endsWith('/') 
    ? IMAGES_BASE_URL.slice(0, -1) 
    : IMAGES_BASE_URL
  
  // Construct the full URL
  const fullUrl = `${baseUrl}/${cleanKey}`
  
  // Use URL constructor to add query parameters safely
  const url = new URL(fullUrl)

  // Add transform parameters
  if (transformOptions) {
    if (transformOptions.width) {
      url.searchParams.set('width', String(transformOptions.width))
    }
    if (transformOptions.height) {
      url.searchParams.set('height', String(transformOptions.height))
    }
    if (transformOptions.format) {
      url.searchParams.set('format', transformOptions.format)
    }
    if (transformOptions.fit) {
      url.searchParams.set('fit', transformOptions.fit)
    }
    if (transformOptions.quality) {
      url.searchParams.set('quality', String(transformOptions.quality))
    }
  }

  return url.toString()
}

/**
 * Build R2 image URL for a specific bucket
 *
 * @param bucket - R2 bucket name
 * @param path - Image path within bucket
 * @param options - Transform options or preset name
 * @returns Full image URL
 *
 * @example
 * buildBucketImageUrl('business-images', 'abc/photo.jpg', 'card')
 */
export function buildBucketImageUrl(
  bucket: ImageBucket,
  path: string,
  options?: ImageTransformOptions | keyof typeof IMAGE_PRESETS
): string {
  return buildImageUrl(`${bucket}/${path}`, options)
}

// ============================================================================
// Placeholder Images
// ============================================================================

const PLACEHOLDER_COLORS = {
  avatar: 'e0e0e0',
  thumbnail: 'f0f0f0',
  card: 'e8e8e8',
  listItem: 'eeeeee',
  detail: 'f5f5f5',
  full: 'fafafa',
  large: 'f8f8f8',
  default: 'e0e0e0',
}

/**
 * Get placeholder image URL
 * Uses a simple data URL for performance
 */
export function getPlaceholderUrl(
  options?: ImageTransformOptions | keyof typeof IMAGE_PRESETS
): string {
  const presetName = typeof options === 'string' ? options : 'default'
  const preset =
    typeof options === 'string' ? IMAGE_PRESETS[options] : options
  const width = preset?.width || 100
  const height = preset?.height || preset?.width || 100
  const color = PLACEHOLDER_COLORS[presetName as keyof typeof PLACEHOLDER_COLORS] || PLACEHOLDER_COLORS.default

  // Generate a simple SVG placeholder
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect fill="#${color}" width="${width}" height="${height}"/><text fill="#999" font-family="sans-serif" font-size="14" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">No Image</text></svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// ============================================================================
// Image Array Helpers
// ============================================================================

/**
 * Get the first image from an array or return placeholder
 *
 * @param photos - Array of image keys
 * @param options - Transform options or preset name
 * @returns URL for first image or placeholder
 */
export function getFirstImage(
  photos: string[] | null | undefined,
  options?: ImageTransformOptions | keyof typeof IMAGE_PRESETS
): string {
  const firstPhoto = photos?.[0]
  return buildImageUrl(firstPhoto, options)
}

/**
 * Build URLs for all images in an array
 *
 * @param photos - Array of image keys
 * @param options - Transform options or preset name
 * @returns Array of image URLs
 */
export function buildImageUrls(
  photos: string[] | null | undefined,
  options?: ImageTransformOptions | keyof typeof IMAGE_PRESETS
): string[] {
  if (!photos || photos.length === 0) {
    return []
  }
  return photos.map((key) => buildImageUrl(key, options))
}

// ============================================================================
// URL Validation
// ============================================================================

/**
 * Check if a URL is an R2 image URL
 */
export function isR2ImageUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.origin === IMAGES_BASE_URL
  } catch {
    return false
  }
}

/**
 * Extract image key from R2 URL
 */
export function extractImageKey(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.origin !== IMAGES_BASE_URL) {
      return null
    }
    // Remove leading slash
    return parsed.pathname.slice(1)
  } catch {
    return null
  }
}

// ============================================================================
// Image Upload API
// ============================================================================

/**
 * Image folder types for organizing uploads
 */
export type ImageFolder = 'marketplace' | 'business' | 'reviews' | 'avatars'

/**
 * Upload response from Image Handler API
 */
export interface UploadedImage {
  key: string
  publicUrl: string
  thumbnailUrl: string
  size: number
  contentType: string
}

/**
 * Upload result
 */
export interface UploadResult {
  success: boolean
  image?: UploadedImage
  error?: string
}

/**
 * Batch upload result
 */
export interface BatchUploadResult {
  success: boolean
  images: Array<UploadedImage & { index: number }>
  errors: Array<{ index: number; error: string }>
}

/**
 * Upload a single image to R2
 * 
 * @param file - File to upload
 * @param folder - Target folder (marketplace, business, reviews, avatars)
 * @returns Upload result with image details
 * 
 * @example
 * const result = await uploadImage(file, 'marketplace');
 * if (result.success) {
 *   console.log('Uploaded:', result.image.publicUrl);
 * }
 */
export async function uploadImage(
  file: File,
  folder: ImageFolder
): Promise<UploadResult> {
  try {
    const token = await getAccessToken()
    if (!token) {
      return { success: false, error: 'Not authenticated' }
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    const response = await fetch(`${IMAGES_API_URL}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      const error = parseImageApiError(response.status, errorBody)
      Sentry.captureException(error, {
        tags: { feature: 'image-upload', action: 'uploadImage' },
        extra: { folder, status: response.status },
      })
      return { success: false, error: error.message }
    }

    const data = await response.json()
    return { success: true, image: data.image }
  } catch (err) {
    const error = new ApiError(
      'Failed to upload image',
      ApiErrorCode.NETWORK_ERROR,
      0
    )
    Sentry.captureException(error, {
      tags: { feature: 'image-upload', action: 'uploadImage' },
      extra: { folder },
    })
    return { success: false, error: error.message }
  }
}

/**
 * Upload multiple images to R2 (max 10)
 * 
 * @param files - Array of files to upload (max 10)
 * @param folder - Target folder
 * @returns Batch upload result with all images
 * 
 * @example
 * const result = await uploadImages(files, 'marketplace');
 * console.log(`Uploaded ${result.images.length} images`);
 */
export async function uploadImages(
  files: File[],
  folder: ImageFolder
): Promise<BatchUploadResult> {
  try {
    if (files.length > 10) {
      return {
        success: false,
        images: [],
        errors: [{ index: 0, error: 'Maximum 10 images allowed per batch' }],
      }
    }

    const token = await getAccessToken()
    if (!token) {
      return {
        success: false,
        images: [],
        errors: [{ index: 0, error: 'Not authenticated' }],
      }
    }

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })
    formData.append('folder', folder)

    const response = await fetch(`${IMAGES_API_URL}/upload/batch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      const error = parseImageApiError(response.status, errorBody)
      Sentry.captureException(error, {
        tags: { feature: 'image-upload', action: 'uploadImages' },
        extra: { folder, count: files.length, status: response.status },
      })
      return {
        success: false,
        images: [],
        errors: [{ index: 0, error: error.message }],
      }
    }

    const data = await response.json()
    return {
      success: true,
      images: data.images || [],
      errors: data.errors || [],
    }
  } catch (err) {
    const error = new ApiError(
      'Failed to upload images',
      ApiErrorCode.NETWORK_ERROR,
      0
    )
    Sentry.captureException(error, {
      tags: { feature: 'image-upload', action: 'uploadImages' },
      extra: { folder },
    })
    return {
      success: false,
      images: [],
      errors: [{ index: 0, error: error.message }],
    }
  }
}

/**
 * Delete an image from R2 (admin only)
 * 
 * @param key - Image key to delete
 * @returns Success status
 */
export async function deleteImage(key: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await getAccessToken()
    if (!token) {
      return { success: false, error: 'Not authenticated' }
    }

    const response = await fetch(`${IMAGES_API_URL}/${key}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      if (response.status === 403) {
        return { success: false, error: 'Admin role required to delete images' }
      }
      if (response.status === 404) {
        // Image doesn't exist, treat as success
        return { success: true }
      }
      const errorBody = await response.json().catch(() => ({}))
      const error = parseImageApiError(response.status, errorBody)
      Sentry.captureException(error, {
        tags: { feature: 'image-upload', action: 'deleteImage' },
        extra: { key, status: response.status },
      })
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err) {
    const error = new ApiError(
      'Failed to delete image',
      ApiErrorCode.NETWORK_ERROR,
      0
    )
    Sentry.captureException(error, {
      tags: { feature: 'image-upload', action: 'deleteImage' },
      extra: { key },
    })
    return { success: false, error: error.message }
  }
}

/**
 * Delete multiple images from R2 (admin only)
 * 
 * @param keys - Array of image keys to delete
 * @returns Results for each deletion
 */
export async function deleteImages(
  keys: string[]
): Promise<{ success: boolean; deleted: string[]; failed: string[] }> {
  const deleted: string[] = []
  const failed: string[] = []

  // Delete sequentially to avoid rate limiting
  for (const key of keys) {
    const result = await deleteImage(key)
    if (result.success) {
      deleted.push(key)
    } else {
      failed.push(key)
    }
  }

  return {
    success: failed.length === 0,
    deleted,
    failed,
  }
}

/**
 * Check if an image exists in R2
 * 
 * @param key - Image key to check
 * @returns True if image exists
 */
export async function imageExists(key: string): Promise<boolean> {
  try {
    const response = await fetch(`${IMAGES_API_URL}/${key}`, {
      method: 'HEAD',
    })
    return response.ok
  } catch {
    return false
  }
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Parse Image API error response
 */
function parseImageApiError(status: number, body: Record<string, unknown>): ApiError {
  const message = (body.error as string) || (body.message as string) || 'Image operation failed'
  
  switch (status) {
    case 401:
      return new ApiError(message, ApiErrorCode.UNAUTHORIZED, status)
    case 403:
      return new ApiError(message, ApiErrorCode.FORBIDDEN, status)
    case 404:
      return new ApiError('Image not found', ApiErrorCode.NOT_FOUND, status)
    case 413:
      return new ApiError('File too large (max 10MB)', ApiErrorCode.VALIDATION_ERROR, status)
    case 415:
      return new ApiError('Unsupported image format', ApiErrorCode.VALIDATION_ERROR, status)
    case 422:
      return new ApiError('Image processing failed', ApiErrorCode.VALIDATION_ERROR, status)
    case 429:
      return new ApiError('Too many requests', ApiErrorCode.RATE_LIMITED, status)
    default:
      return new ApiError(message, ApiErrorCode.INTERNAL_ERROR, status)
  }
}
